import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IContact } from "#app/components/Cabinet/Chat/Contacts/Contacts";
import { TMessage } from "#app/components/Cabinet/Chat/Messages/types";

interface ChatState {
    contacts: IContact[];
    contactsLoaded: boolean;
    contactsError: string | null; // Ошибка загрузки контактов
    messages: Record<string, TMessage[]>; // chatId -> messages
    muted: Record<string, boolean>; // chatId -> muted state
    currentChatId: string;
    userId: number | null;
}

const initialState: ChatState = {
    contacts: [],
    contactsLoaded: false,
    contactsError: null,
    messages: {},
    muted: {},
    currentChatId: localStorage.getItem("last_chat_id") || "",
    userId: null,
};

const chatSlice = createSlice({
    name: "chat",
    initialState,
    reducers: {
        setContacts(state, action: PayloadAction<IContact[]>) {
            state.contacts = action.payload;
        },
        setContactsLoaded(state, action: PayloadAction<boolean>) {
            state.contactsLoaded = action.payload;
            // При успешной загрузке очищаем ошибку
            if (action.payload) {
                state.contactsError = null;
            }
        },
        setContactsError(state, action: PayloadAction<string | null>) {
            state.contactsError = action.payload;
            // При установке ошибки помечаем, что загрузка завершена (но с ошибкой)
            state.contactsLoaded = true;
        },
        addContact(state, action: PayloadAction<IContact>) {
            const existingIndex = state.contacts.findIndex(
                (c) => c.chatId === action.payload.chatId
            );
            if (existingIndex >= 0) {
                state.contacts[existingIndex] = action.payload;
            } else {
                state.contacts.push(action.payload);
            }
        },
        updateContact(
            state,
            action: PayloadAction<{ chatId: string; updates: Partial<IContact> }>
        ) {
            const contact = state.contacts.find((c) => c.chatId === action.payload.chatId);
            if (contact) {
                Object.assign(contact, action.payload.updates);
            }
        },
        setMessages(state, action: PayloadAction<{ chatId: string; messages: TMessage[] }>) {
            state.messages[action.payload.chatId] = action.payload.messages;
        },
        addMessage(state, action: PayloadAction<{ chatId: string; message: TMessage }>) {
            if (!state.messages[action.payload.chatId]) {
                state.messages[action.payload.chatId] = [];
            }
            state.messages[action.payload.chatId].push(action.payload.message);
        },
        setCurrentChatId(state, action: PayloadAction<string>) {
            state.currentChatId = action.payload;
            localStorage.setItem("last_chat_id", action.payload);
        },
        setMuted(state, action: PayloadAction<{ chatId: string; muted: boolean }>) {
            state.muted[action.payload.chatId] = action.payload.muted;
        },
        setUserId(state, action: PayloadAction<number | null>) {
            state.userId = action.payload;
        },
        clearChat(state) {
            state.contacts = [];
            state.contactsLoaded = false;
            state.contactsError = null;
            state.messages = {};
            state.muted = {};
            state.currentChatId = "";
        },
        clearMessagesForChat(state, action: PayloadAction<string>) {
            delete state.messages[action.payload];
        },
    },
});

export const {
    setContacts,
    setContactsLoaded,
    setContactsError,
    addContact,
    updateContact,
    setMessages,
    addMessage,
    setCurrentChatId,
    setMuted,
    setUserId,
    clearChat,
    clearMessagesForChat,
} = chatSlice.actions;

export default chatSlice.reducer;
